import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { importLegacyCart } from "../../api/carts";
import { ApiError } from "../../lib/api";
import { InvalidStoredCartError, loadCartState } from "../../lib/priceRequest";
import { scopedBrowserStorageKey, type StorageIdentity } from "../../lib/scopedBrowserStorage";

/** Mount with a key for the active tenant/user. The server owns completion;
 * localStorage is only a shortcut and the original draft is never removed.
 */
export default function LegacyPriceRequestImport({ identity, onSelect }: {
  identity: StorageIdentity;
  onSelect: (cartId: string) => void;
}) {
  const queryClient = useQueryClient();
  const select = useRef(onSelect);
  select.current = onSelect;
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<{ text: string; retry: boolean } | null>(null);
  const request = useRef<{ attempt: number; promise: ReturnType<typeof importLegacyCart> } | null>(null);
  const { customer_id, user_id } = identity;

  useEffect(() => {
    let active = true;
    const scope = { customer_id, user_id };
    const doneKey = scopedBrowserStorageKey("truucost.cart-import.v1", scope)!;
    try {
      if (localStorage.getItem(doneKey) === "1") return;
      const lines = loadCartState(scope, { throwOnError: true }).lines;
      if (lines.length === 0) return;
      setNotice({ text: "Deine frühere Preisanfrage wird übernommen …", retry: false });
      // React's effect replay shares the in-flight request. Across navigation,
      // reloads and tabs the durable server receipt provides the same safety.
      if (request.current?.attempt !== attempt) {
        request.current = { attempt, promise: importLegacyCart(lines.map(line => ({ article_group_key: line.articleGroupKey, qty: line.qty }))) };
      }
      void request.current.promise.then(result => {
        if (!active) return;
        try { localStorage.setItem(doneKey, "1"); } catch { /* The server receipt protects subsequent reloads. */ }
        setNotice({ text: result.cart_id ? "Deine frühere Preisanfrage wurde übernommen." : "Deine frühere Preisanfrage wurde bereits übernommen. Der damalige Warenkorb wurde inzwischen gelöscht.", retry: false });
        void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
        if (result.cart_id) select.current(result.cart_id);
      }).catch(error => {
        if (!active) return;
        setNotice({ text: error instanceof ApiError ? error.message : "Die Übernahme konnte nicht bestätigt werden. Deine Browser-Vorlage bleibt erhalten. Versuche es erneut; bereits übernommene Positionen werden nicht verdoppelt.", retry: true });
      });
    } catch (error) {
      setNotice({ text: error instanceof InvalidStoredCartError
        ? "Deine gespeicherte Preisanfrage enthält unvollständige oder ungültige Positionen. Es wurde nichts übernommen. Deine Browser-Vorlage bleibt erhalten. Prüfe die Positionen vor der Übernahme."
        : "Deine frühere Preisanfrage konnte nicht aus dem Browser gelesen werden. Erlaube den Zugriff auf den Browserspeicher und versuche es erneut.", retry: true });
    }
    return () => { active = false; };
  }, [customer_id, user_id, attempt, queryClient]);

  if (!notice) return null;
  return (
    <div role={notice.retry ? "alert" : "status"} className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
      <p>{notice.text}</p>
      {notice.retry ? <button type="button" className="mt-2 h-9 rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm hover:bg-slate-50" onClick={() => setAttempt(value => value + 1)}>Übernahme erneut versuchen</button> : null}
    </div>
  );
}
